/**
 * @module errorCapture
 * @description Global error capture service for production monitoring and debugging
 * 
 * This service provides comprehensive error tracking with:
 * - **Deduplication**: Same errors within 1 minute are ignored to prevent spam
 * - **Rate Limiting**: Maximum 10 errors per minute to protect backend
 * - **Classification**: Automatic categorization (runtime, promise_rejection, chunk_load, react_boundary)
 * - **Backend Logging**: Errors are sent to Supabase edge function for persistence
 * 
 * @example
 * // Initialize globally in App.tsx or main.tsx
 * import { initializeErrorCapture } from '@/services/errorCapture';
 * 
 * useEffect(() => {
 *   const cleanup = initializeErrorCapture();
 *   return cleanup;
 * }, []);
 * 
 * @example
 * // Manual error capture (e.g., in error boundaries)
 * import { captureError } from '@/services/errorCapture';
 * 
 * try {
 *   await riskyOperation();
 * } catch (error) {
 *   captureError(error, { componentStack }, 'runtime');
 * }
 * 
 * @architecture
 * ```
 * Client Error → captureError() → Dedup Check → Rate Limit → Edge Function → Database
 *                                     ↓              ↓
 *                                  (skip)        (skip)
 * ```
 * 
 * @see GlobalErrorCapture - React component that initializes this service
 * @see log-publish-error - Edge function that receives error reports
 * @since 1.0.0
 */

import { supabase } from '@/integrations/supabase/client';

/**
 * @interface CapturedError
 * @description Structure of error data sent to the backend for logging
 */
interface CapturedError {
  /** Human-readable error message */
  error_message: string;
  
  /** Classification of the error source */
  error_type: 'runtime' | 'promise_rejection' | 'react_boundary' | 'chunk_load';
  
  /** Additional context about the error */
  error_details: {
    /** Full stack trace if available */
    stack?: string;
    /** React component stack for boundary errors */
    componentStack?: string;
    /** Browser user agent string */
    userAgent?: string;
    /** URL where error occurred */
    url?: string;
    /** ISO timestamp of error */
    timestamp?: string;
    /** Current route path */
    route?: string;
    /** Additional debug context */
    context?: string;
  };
  
  /** Source file path extracted from stack trace */
  file_path?: string;
  
  /** Line number in source file */
  line_number?: number;
  
  /** Current environment */
  environment: 'production' | 'development';
  
  /** User ID if authenticated */
  triggered_by?: string;
}

// ============================================================================
// DEDUPLICATION & RATE LIMITING
// ============================================================================

/** Cache storing error signatures with timestamps for deduplication */
const errorCache = new Map<string, number>();

/** Duration in ms to consider errors as duplicates */
const CACHE_DURATION = 60000; // 1 minute

/** Maximum errors allowed per minute before rate limiting */
const MAX_ERRORS_PER_MINUTE = 10;

/** Counter for rate limiting */
let errorCount = 0;

/** Timestamp of last rate limit reset */
let lastResetTime = Date.now();

/**
 * @function getErrorSignature
 * @description Creates a unique signature for error deduplication
 * 
 * Combines error message with first stack trace line to identify unique errors
 * while treating same errors from same location as duplicates.
 * 
 * @param {string} message - Error message
 * @param {string} [stack] - Stack trace
 * @returns {string} Unique signature for this error (max 200 chars)
 * 
 * @example
 * const sig = getErrorSignature('TypeError: x is undefined', 'at Component (file.tsx:10)');
 * // Returns: "TypeError: x is undefined::at Component (file.tsx:10)"
 */
function getErrorSignature(message: string, stack?: string): string {
  const stackLine = stack?.split('\n')[1] || '';
  return `${message}::${stackLine}`.substring(0, 200);
}

/**
 * @function shouldCaptureError
 * @description Determines if an error should be captured based on deduplication and rate limiting
 * 
 * @param {string} signature - Error signature from getErrorSignature()
 * @returns {boolean} True if error should be captured, false if it should be skipped
 * 
 * @sideEffects
 * - Updates errorCache with new timestamp
 * - Increments errorCount
 * - May reset rate limit counter
 */
function shouldCaptureError(signature: string): boolean {
  const now = Date.now();
  
  // Reset rate limit counter every minute
  if (now - lastResetTime > 60000) {
    errorCount = 0;
    lastResetTime = now;
  }
  
  // Check rate limit
  if (errorCount >= MAX_ERRORS_PER_MINUTE) {
    console.warn('[ErrorCapture] Rate limit exceeded, skipping error');
    return false;
  }
  
  // Check deduplication cache
  const lastSeen = errorCache.get(signature);
  if (lastSeen && now - lastSeen < CACHE_DURATION) {
    return false; // Skip duplicate
  }
  
  // Update cache and counter
  errorCache.set(signature, now);
  errorCount++;
  
  return true;
}

/**
 * @function determineErrorType
 * @description Automatically classifies error type based on error message patterns
 * 
 * @param {Error} error - The error to classify
 * @returns {CapturedError['error_type']} Classification of the error
 * 
 * @example
 * determineErrorType(new Error('Failed to fetch dynamically imported module'))
 * // Returns: 'chunk_load'
 */
function determineErrorType(error: Error): CapturedError['error_type'] {
  const message = error.message.toLowerCase();
  
  // Detect chunk loading errors (common in SPAs with code splitting)
  if (
    message.includes('failed to fetch dynamically imported module') ||
    message.includes('importing a module script failed') ||
    message.includes('loading chunk')
  ) {
    return 'chunk_load';
  }
  
  return 'runtime';
}

// ============================================================================
// MAIN API
// ============================================================================

/**
 * @function captureError
 * @description Captures and logs an error to the backend monitoring system
 * 
 * This is the main entry point for error capture. It handles:
 * - Deduplication to prevent spam
 * - Rate limiting to protect the backend
 * - User context extraction
 * - Stack trace parsing
 * - Async backend submission
 * 
 * @param {Error} error - The error to capture
 * @param {Object} [errorInfo] - Additional React error info
 * @param {string} [errorInfo.componentStack] - React component stack trace
 * @param {CapturedError['error_type']} [errorType] - Override automatic type detection
 * 
 * @returns {Promise<void>} Resolves when error is logged (or skipped)
 * 
 * @example
 * // In an error boundary
 * componentDidCatch(error: Error, errorInfo: ErrorInfo) {
 *   captureError(error, errorInfo, 'react_boundary');
 * }
 * 
 * @example
 * // Manual capture with custom type
 * try {
 *   await fetchData();
 * } catch (error) {
 *   await captureError(error as Error, undefined, 'runtime');
 * }
 * 
 * @sideEffects
 * - Makes async call to log-publish-error edge function
 * - Logs to console on success/failure
 */
export async function captureError(
  error: Error,
  errorInfo?: { componentStack?: string },
  errorType?: CapturedError['error_type']
): Promise<void> {
  try {
    const signature = getErrorSignature(error.message, error.stack);
    
    // Skip if duplicate or rate limited
    if (!shouldCaptureError(signature)) {
      return;
    }
    
    // Get current user if authenticated
    const { data: { user } } = await supabase.auth.getUser();
    
    // Extract file path and line number from stack trace
    // Pattern: "at FunctionName (file.tsx:123:45)"
    const stackMatch = error.stack?.match(/at .+ \((.+):(\d+):\d+\)/);
    const filePath = stackMatch?.[1];
    const lineNumber = stackMatch?.[2] ? parseInt(stackMatch[2]) : undefined;
    
    const capturedError: CapturedError = {
      error_message: error.message,
      error_type: errorType || determineErrorType(error),
      error_details: {
        stack: error.stack,
        componentStack: errorInfo?.componentStack || null,
        userAgent: navigator.userAgent,
        url: window.location.href,
        timestamp: new Date().toISOString(),
        route: window.location.pathname,
        context: errorInfo?.componentStack 
          ? `Component stack available - check componentStack field`
          : `No component stack - error may be outside React tree`,
      },
      file_path: filePath,
      line_number: lineNumber,
      environment: import.meta.env.MODE as 'production' | 'development',
      triggered_by: user?.id,
    };
    
    // Send to backend
    const { error: logError } = await supabase.functions.invoke('log-publish-error', {
      body: capturedError,
    });
    
    if (logError) {
      console.error('[ErrorCapture] Failed to log error to backend:', logError);
    } else {
      console.log('[ErrorCapture] Error logged successfully:', signature);
    }
  } catch (captureError) {
    // SECURITY: Never let error capture crash the app
    console.error('[ErrorCapture] Failed to capture error:', captureError);
  }
}

/**
 * @function initializeErrorCapture
 * @description Initializes global error handlers for automatic error capture
 * 
 * Sets up listeners for:
 * - `window.error` - Uncaught JavaScript errors
 * - `window.unhandledrejection` - Unhandled Promise rejections
 * 
 * @returns {() => void} Cleanup function to remove event listeners
 * 
 * @example
 * // In App.tsx or main.tsx
 * useEffect(() => {
 *   const cleanup = initializeErrorCapture();
 *   return cleanup; // Remove listeners on unmount
 * }, []);
 * 
 * @sideEffects
 * - Adds global event listeners
 * - Logs initialization to console
 */
export function initializeErrorCapture(): () => void {
  console.log('[ErrorCapture] Initializing global error capture');
  
  // Handle uncaught JavaScript errors
  const errorHandler = (event: ErrorEvent) => {
    console.error('[ErrorCapture] Uncaught error:', event.error);
    captureError(event.error || new Error(event.message), undefined, 'runtime');
  };
  
  // Handle unhandled Promise rejections
  const rejectionHandler = (event: PromiseRejectionEvent) => {
    console.error('[ErrorCapture] Unhandled rejection:', event.reason);
    const error = event.reason instanceof Error 
      ? event.reason 
      : new Error(String(event.reason));
    captureError(error, undefined, 'promise_rejection');
  };
  
  window.addEventListener('error', errorHandler);
  window.addEventListener('unhandledrejection', rejectionHandler);
  
  // Return cleanup function
  return () => {
    window.removeEventListener('error', errorHandler);
    window.removeEventListener('unhandledrejection', rejectionHandler);
    errorCache.clear();
  };
}
